<template>
    <v-card class="rounded-lg mb-4" elevation="2">
        <v-card-title class="py-3 px-4 d-flex align-center justify-space-between grey lighten-5">
            <div class="d-flex align-center">
                <v-icon left color="primary">{{ icon }}</v-icon>
                {{ title }}
            </div>
             <v-btn-toggle
                v-model="channelTab"
                mandatory
                rounded
                dense
                color="primary"
                class="my-0"
            >
                <v-btn :value="0" small>
                    <v-icon small left>mdi-cellphone</v-icon> App
                </v-btn>
                <v-btn :value="1" small :disabled="!whatsappEnabled">
                    <v-icon small left>mdi-whatsapp</v-icon> WhatsApp
                </v-btn>
            </v-btn-toggle>
        </v-card-title>
        <v-divider></v-divider>
        
        <v-card-text class="pa-4">
            <v-row>
                <v-col cols="12" md="7" lg="8">
                    <!-- Chips for Variables -->
                    <div class="mb-4">
                        <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2">
                            <v-icon x-small left>mdi-variable</v-icon>
                            VARIÁVEIS (Clique para inserir)
                        </div>
                        <div class="d-flex flex-wrap" style="gap: 8px;">
                            <v-chip
                                v-for="v in displayVariables"
                                :key="v.key"
                                small
                                label
                                color="primary"
                                outlined
                                @click="insertVariable(v)"
                                style="cursor: pointer"
                            >
                                <strong>{{ v.label }}</strong>
                            </v-chip>
                        </div>
                    </div>

                    <!-- Form Fields - Appointment -->
                    <div v-if="sectionType === 'appointment'">
                        <div v-if="!isWhatsapp">
                            <v-textarea
                                v-model="localSettings.app_message_24h"
                                label="Lembrete 24h antes"
                                outlined
                                dense
                                rows="3"
                                hint="Push notification para o app"
                                @focus="onFocus('app_message_24h')"
                            ></v-textarea>
                            <v-textarea
                                v-model="localSettings.app_message_1h"
                                label="Lembrete 1h antes"
                                outlined
                                dense
                                rows="3"
                                @focus="onFocus('app_message_1h')"
                            ></v-textarea>
                        </div>
                        <div v-else>
                            <v-textarea
                                v-model="localSettings.whatsapp_message_24h"
                                label="Msg WhatsApp 24h antes"
                                outlined
                                dense
                                rows="4"
                                hint="Use *texto* para negrito"
                                @focus="onFocus('whatsapp_message_24h')"
                            ></v-textarea>
                            <v-textarea
                                v-model="localSettings.whatsapp_message_1h"
                                label="Msg WhatsApp 1h antes"
                                outlined
                                dense
                                rows="4"
                                @focus="onFocus('whatsapp_message_1h')"
                            ></v-textarea>
                        </div>
                    </div>

                    <!-- Form Fields - Payment -->
                    <div v-if="sectionType === 'payment'">
                        <div v-if="!isWhatsapp">
                            <v-textarea
                                v-model="localSettings.payment_app_message_1day"
                                label="Aviso 1 dia antes"
                                outlined
                                dense
                                rows="3"
                                @focus="onFocus('payment_app_message_1day')"
                            ></v-textarea>
                            <v-textarea
                                v-model="localSettings.payment_app_message_day"
                                label="Aviso no dia"
                                outlined
                                dense
                                rows="3"
                                @focus="onFocus('payment_app_message_day')"
                            ></v-textarea>
                        </div>
                        <div v-else>
                            <v-textarea
                                v-model="localSettings.payment_whatsapp_message_1day"
                                label="WhatsApp 1 dia antes"
                                outlined
                                dense
                                rows="4"
                                @focus="onFocus('payment_whatsapp_message_1day')"
                            ></v-textarea>
                            <v-textarea
                                v-model="localSettings.payment_whatsapp_message_day"
                                label="WhatsApp no dia"
                                outlined
                                dense
                                rows="4"
                                @focus="onFocus('payment_whatsapp_message_day')"
                            ></v-textarea>
                        </div>
                    </div>
                </v-col>

                <!-- Live Preview Column -->
                <v-col cols="12" md="5" lg="4" class="grey lighten-5 rounded-r-lg d-flex flex-column align-center justify-center">
                    <div class="text-overline grey--text mb-2">Simulação (Exemplo)</div>
                    <notification-preview
                        :title="isWhatsapp ? 'WhatsApp' : 'App'"
                        :body="currentPreview"
                        :type="isWhatsapp ? 'whatsapp' : 'app'"
                    />
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<script>
import NotificationPreview from './NotificationPreview.vue';

export default {
    name: 'SettingsSectionCard',
    components: { NotificationPreview },
    props: ['title', 'icon', 'variables', 'settings', 'whatsappEnabled', 'sectionType', 'previewData'],
    data() {
        return {
            channelTab: 0, // 0: App, 1: WhatsApp
            focusedField: null,
            localSettings: { ...this.settings } // Reactive copy
        };
    },
    watch: {
        settings: {
            handler(val) { this.localSettings = val; },
            deep: true
        },
        localSettings: {
            handler(val) { this.$emit('update', val); },
            deep: true
        }
    },
    computed: {
        isWhatsapp() { return this.channelTab === 1; },
        currentPreview() {
            // Determine what text to show in preview based on active tab and potentially focused field preference
            // For MVP, we show the main message of the tab (e.g. 24h)
            if (this.sectionType === 'appointment') {
                const text = this.isWhatsapp ? this.localSettings.whatsapp_message_24h : this.localSettings.app_message_24h;
                return this.replaceVariables(text || '', this.previewData);
            } else {
                const text = this.isWhatsapp ? this.localSettings.payment_whatsapp_message_1day : this.localSettings.payment_app_message_1day;
                return this.replaceVariables(text || '', this.previewData);
            }
        },
        displayVariables() {
            return this.variables;
        }
    },
    methods: {
        insertVariable(variable) {
            if (!this.focusedField) return;
            const field = this.focusedField;
            if (this.localSettings[field] === undefined) return;
            
            // Append to end if simple, or could improve to insert at cursor
            this.localSettings[field] += ` {{${variable.key}}}`;
        },
        onFocus(field) {
            this.focusedField = field;
        },
        replaceVariables(text, data) {
            let replaced = text;
            Object.keys(data).forEach(key => {
                const regex = new RegExp(`{{${key}}}`, 'g');
                replaced = replaced.replace(regex, data[key]);
            });
            return replaced;
        }
    }
};
</script>

<style scoped>
/* Add transition for smooth toggle */
.v-btn-toggle {
    transition: all 0.3s ease;
}
</style>
